<template>
    <e-modal
        v-model="isModalOpen"
        class="is-modal-select"
        :width="width"
        :style="{ width: modalWidth }"
        :destroy-on-hide="false"
        trap-focus
        has-modal-card
        aria-role="dialog"
        aria-modal
        @close="close"
    >
        <form @submit.prevent="handleFormSubmit">
            <div>
                <header class="e-modal-header">
                    <p class="e-modal-title">
                        <slot name="title">
                            <span v-if="limit === 1">Select {{ indefiniteArticle }} {{ capitalCase(label) }}</span>
                            <span v-else>Select {{ pluralize(capitalCase(label), limit) }}</span>
                        </slot>
                    </p>
                    <p v-if="minimum !== 1 && limit > 1" class="tw:text-muted-color">
                        <span v-if="minimum === limit">
                            You may select {{ limit }}
                            {{ pluralize(label, limit) }}
                        </span>
                        <span v-else>
                            You may select {{ minimum }}-{{ limit }}
                            {{ pluralize(label, limit) }}
                        </span>
                    </p>
                </header>
                <section class="e-modal-body" :style="{ minHeight: bodyMinHeight, maxHeight: bodyMaxHeight }">
                    <section v-if="retrieving || loading" class="e-modal-loader">
                        <e-progress type="is-primary" size="is-small" style="height: 2px" />
                    </section>

                    <slot name="main">
                        <div class="tw:grid tw:gap-4">
                            <!-- Controls Section -->
                            <div class="tw:flex tw:flex-col sm:tw:flex-row tw:gap-4 tw:items-start sm:tw:items-center tw:justify-between">
                                <div class="tw:flex tw:flex-wrap tw:gap-2 tw:items-center">
                                    <!-- View Toggle -->
                                    <div v-if="availableView.length === 2">
                                        <e-field>
                                            <p class="control">
                                                <button
                                                    :class="{
                                                        'is-primary': currentView === 'thumbnail',
                                                    }"
                                                    class="button"
                                                    @click.prevent="currentView = 'thumbnail'"
                                                >
                                                    <e-icon icon="apps" />
                                                </button>
                                            </p>
                                            <p class="control">
                                                <button
                                                    :class="{
                                                        'is-primary': currentView === 'detail',
                                                    }"
                                                    class="button"
                                                    @click.prevent="currentView = 'detail'"
                                                >
                                                    <e-icon icon="bars" />
                                                </button>
                                            </p>
                                        </e-field>
                                    </div>

                                    <!-- Filters -->
                                    <div v-if="filters.length">
                                        <e-field position="is-left">
                                            <p class="control">
                                                <filter-dropdown
                                                    :key="isModalOpen"
                                                    :tag-type="tagType"
                                                    :filters="filters"
                                                    :manually-applied-filters="
                                                        appliedFilter ? [appliedFilter] : null
                                                    "
                                                    @apply-filter="onFilter"
                                                    @clear-filters="onClearFilters"
                                                />
                                            </p>
                                        </e-field>
                                    </div>

                                    <!-- Sorts -->
                                    <div v-if="sorts.length">
                                        <e-field position="is-left">
                                            <p class="control">
                                                <modal-sorts
                                                    v-if="sorts.length > 0"
                                                    :sorts="sorts"
                                                    :current-sort="sortingField"
                                                    :current-order="sortingOrder"
                                                    @apply-sort="onSort"
                                                />
                                            </p>
                                        </e-field>
                                    </div>

                                    <slot name="additional-controls" />
                                </div>

                                <!-- Search -->
                                <div v-if="searchable" class="tw:w-full sm:tw:w-auto">
                                    <modal-search v-model="searchQuery" />
                                </div>
                            </div>

                            <!-- Content Section -->
                            <div>
                                <!-- Thumbnail View -->
                                <div
                                    v-if="items.length && currentView === 'thumbnail' && includes(availableView, 'thumbnail')"
                                    class="columns is-multiline is-mobile"
                                >
                                    <template v-for="(item, itemIndex) in items">
                                        <slot
                                            name="thumbnail-view"
                                            :item="item"
                                            :index="itemIndex"
                                            :is-selected="isSelected(item)"
                                            :select-item="selectItem"
                                            :is-row-checkable="canSelectItem"
                                            :current-view="currentView"
                                            :default-sort-direction="defaultSortOrder"
                                            :default-sort="[sortField, sortOrder]"
                                            :sortable="sortable"
                                        />
                                    </template>
                                </div>

                                <!-- Detail View -->
                                <div v-else-if="items.length && currentView === 'detail' && includes(availableView, 'detail')">
                                    <slot
                                        name="detail-view"
                                        :items="items"
                                        :selected="selected"
                                        :select-item="selectItem"
                                        :select-all-state="selectAllState"
                                        :select-all-disabled="selectAllDisabled"
                                        :select-all-disabled-reason="selectAllDisabledReason"
                                        :default-sort-direction="defaultSortOrder"
                                        :default-sort="[sortField, sortOrder]"
                                        :sortable="sortable"
                                    />
                                </div>

                                <!-- Empty State -->
                                <div v-else-if="!retrieving && !loading">
                                    <slot name="empty">
                                        <p class="tw:text-muted-color">No {{ pluralize(label, 0) }} to display.</p>
                                    </slot>
                                </div>
                            </div>


                            <!-- Pagination -->
                            <div v-if="pagination.total">
                                <slot :pagination="pagination" name="pagination">
                                    <e-pagination
                                        v-model="pagination.current_page"
                                        :total="pagination.total"
                                        :range-before="1"
                                        :range-after="1"
                                        :per-page="pagination.per_page"
                                        icon-prev="angle-left-b"
                                        icon-next="angle-right-b"
                                        aria-next-label="Next page"
                                        aria-previous-label="Previous page"
                                        aria-page-label="Page"
                                        aria-current-label="Current page"
                                        @change="changePage"
                                    />
                                </slot>
                            </div>
                        </div>
                    </slot>
                </section>
                <footer class="e-modal-footer">
                    <span v-if="allSelected.length && limit > 1" class="has-text-right tw:text-muted-color m-r-md">
                        {{ allSelected.length }}
                        {{ pluralize(label, allSelected.length) }} selected
                    </span>
                    <div class="tw:flex tw:gap-2">
                        <slot name="close">
                            <e-button type="button" @click="close">
                                <slot name="cancel-label">Cancel</slot>
                            </e-button>
                        </slot>
                        <slot name="save">
                            <e-button
                                type="is-primary"
                                native-type="submit"
                                :disabled="internallyDisabled"
                                :loading="submitting"
                            >
                                <slot name="choose-label">Choose</slot>
                            </e-button>
                        </slot>
                    </div>
                </footer>
            </div>
        </form>
    </e-modal>
</template>

<script>
import { capitalCase } from 'change-case';
import castArray from 'lodash/castArray.js';
import debounce from 'lodash/debounce.js';
import filter from 'lodash/filter.js';
import find from 'lodash/find.js';
import includes from 'lodash/includes.js';
import isEmpty from 'lodash/isEmpty.js';
import reject from 'lodash/reject.js';
import take from 'lodash/take.js';
import takeRight from 'lodash/takeRight.js';
import uniqBy from 'lodash/uniqBy.js';
import pluralize from 'pluralize';

import FilterDropdown from '@/components/FilterDropdown.vue';
import ModalSearch from '@/components/modal-select/base/ModalSearch.vue';
import ModalSorts from '@/components/modal-select/base/ModalSorts.vue';
import FilterEvent from '@/components/modal-select/filters/FilterEvent.js';
import { handleResponseError } from '@/Helpers.js';
import axios from '@/plugins/axios.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default {
    name: 'ModalSelect',

    components: { FilterDropdown, ModalSorts, ModalSearch },

    props: {
        availableView: {
            type: Array,
            required: false,
            default: () => ['thumbnail', 'detail'],
        },

        baseView: {
            type: String,
            required: false,
            default: 'thumbnail',
        },

        open: {
            type: Boolean,
            required: true,
            default: true,
        },

        loading: {
            type: Boolean,
            required: false,
            default: false,
        },

        endpoint: {
            type: String,
            required: false,
            default: '',
        },

        filters: {
            type: Array,
            required: false,
            default: () => [],
        },

        appliedFilter: {
            required: false,
            validator: prop => prop instanceof FilterEvent || prop === null,
            default: null,
        },

        sorts: {
            type: Array,
            required: false,
            default: () => [],
        },

        sortable: {
            type: Boolean,
            default: true,
        },

        sortField: {
            type: String,
            required: false,
            default: 'id',
        },

        sortOrder: {
            type: String,
            required: false,
            default: 'asc',
        },

        defaultSortOrder: {
            type: String,
            required: false,
            default: 'asc',
        },

        searchable: {
            type: Boolean,
            default: true,
        },

        alreadySelected: {
            type: Array,
            required: false,
            default: () => [],
        },

        limit: {
            type: Number,
            required: false,
            default: 1,
        },

        minimum: {
            type: Number,
            required: false,
            default: 1,
        },

        useKey: {
            type: String,
            default: 'id',
        },

        label: {
            type: String,
            required: false,
            default: 'item',
        },

        data: {
            type: Array,
            required: false,
            default: () => [],
        },

        additionalFilter: {
            type: [Object, Array],
            required: false,
            default: () => {},
        },

        additionalFilter2: {
            type: [Array],
            required: false,
            default: () => {},
        },

        additionalInclude: {
            type: Array,
            required: false,
            default: () => [],
        },

        additionalRaw: {
            type: Object,
            required: false,
            default: () => ({}),
        },

        tagType: {
            type: String,
            required: false,
            default: '',
        },

        afterQuery: {
            type: [Function, Array],
            required: false,
            default: () => () => true,
        },

        model: {
            required: false,
            validator: prop => typeof prop === 'function' || prop === null,
            default: null,
        },

        closeOnChoose: {
            type: Boolean,
            required: false,
            default: true,
        },

        disabled: {
            type: Boolean,
            required: false,
            default: false,
        },

        unselectable: {
            type: Array,
            required: false,
            default: () => [],
        },

        submitting: {
            type: Boolean,
            required: false,
            default: false,
        },

        width: {
            type: Number,
            default: 960,
        },

        modalWidth: {
            type: String,
            required: false,
            default: '75vw',
        },

        bodyMinHeight: {
            type: String,
            required: false,
            default: '70vh',
        },

        bodyMaxHeight: {
            type: String,
            required: false,
            default: '70vh',
        },
    },

    emits: [
        'removing',
        'removed',
        'adding',
        'added',
        'filtering',
        'filtered',
        'searching',
        'searched',
        'chosen',
        'close',
        'sorting',
        'sorted',
        'retrieved',
        'retrieving',
        'selected',
    ],

    data: () => ({
        retrieving: false,
        isModalOpen: false,
        selected: [],
        allSelected: [],
        currentView: null,
        searchQuery: null,
        sortingField: null,
        sortingOrder: null,
        queryBuilder: null,
        pagination: { current_page: 1, total: 0, per_page: 0 },
        rawItems: [],
        latestRequestId: 0,
    }),

    computed: {
        items() {
            let items;

            if (this.model && this.queryBuilder) {
                items = this.queryBuilder.applyToModel(this.model, this.pagination).get();
            } else {
                items = this.rawItems || [];
            }

            if (this.afterQuery) {
                items = items.filter(this.afterQuery);
            }

            return items;
        },

        indefiniteArticle() {
            return ['a', 'e', 'i', 'o', 'u'].includes(this.label.charAt(0).toLowerCase()) ? 'an' : 'a';
        },

        allowsMultiple() {
            return this.limit >= 1;
        },

        eventPayload() {
            return { queryBuilder: this.queryBuilder, items: this.items };
        },

        internallyDisabled() {
            return this.selected.length < this.minimum || this.disabled;
        },

        selectableItems() {
            return this.items.filter(item => !this.unselectable.includes(item[this.useKey]));
        },

        selectionCap() {
            return this.limit > 1 ? Math.min(this.selectableItems.length, this.limit) : this.selectableItems.length;
        },

        selectAllState() {
            if (this.selectableItems.length === 0) {
                return 'none';
            }

            const selectedCount = this.selectableItems.filter(item => this.isSelected(item)).length;

            if (selectedCount === 0) {
                return 'none';
            }

            return selectedCount >= this.selectionCap ? 'all' : 'some';
        },

        selectAllDisabled() {
            if (this.limit <= 1) {
                return false;
            }

            if (this.selectAllState === 'all') {
                return false;
            }

            const pageAdditions = this.selectableItems.filter(item => !this.isSelected(item)).length;

            return this.allSelected.length + pageAdditions > this.limit;
        },

        selectAllDisabledReason() {
            if (!this.selectAllDisabled) {
                return '';
            }

            return `Selecting all would exceed the maximum of ${this.limit} ${pluralize(this.label, this.limit)} allowed.`;
        },
    },

    watch: {
        $props: {
            immediate: true,
            handler() {
                if (this.minimum > this.limit && this.limit > 0) {
                    throw new Error('Error in modal-select: The minimum must be less than the limit');
                }
            },
        },

        appliedFilter: {
            immediate: true,

            handler(appliedFilter) {
                if (appliedFilter) {
                    this.onFilter(appliedFilter);
                }
            },
        },

        async open(open) {
            this.isModalOpen = open;
            if (open) {
                // Reset to committed selection immediately so cancelled picks never flash
                this.resetSelectionToCommitted();

                if (this.endpoint) {
                    this.setQueryBuilder();
                    await this.retrieveItems();
                } else {
                    this.rawItems = Array.isArray(this.data) ? this.data : [];
                    await this.setInitialSelection();
                }
            }
        },

        items(items) {
            this.selected = reject(
                this.selected,
                selectedItem => !items.map(item => item[this.useKey]).includes(selectedItem[this.useKey])
            );
        },

        searchQuery(query) {
            this.search(query);
        },

        selected(selected) {
            this.$emit('selected', selected);
        },
    },

    async created() {
        if (this.endpoint) {
            this.setQueryBuilder();
        }

        this.sortingField = this.sortField;
        this.sortingOrder = this.sortOrder;

        this.currentView = this.baseView;
    },

    methods: {
        capitalCase,
        includes,
        pluralize,

        setQueryBuilder() {
            this.queryBuilder = new QueryBuilder(this.endpoint);

            // Apply initial sort
            if (this.sortingField && this.sortingOrder) {
                this.queryBuilder.sort(this.sortingField, this.sortingOrder);
            }

            if (this.additionalFilter) {
                this.queryBuilder.filters(castArray(this.additionalFilter));
            }

            if (!isEmpty(this.additionalFilter2)) {
                this.additionalFilter2.forEach(item => this.queryBuilder.filter(item.field, item.value));
            }

            if (this.additionalInclude) {
                this.queryBuilder.include(this.additionalInclude);
            }

            if (this.additionalRaw && !isEmpty(this.additionalRaw)) {
                this.queryBuilder.raw(this.additionalRaw);
            }

            this.filters.forEach(modalFilter => modalFilter.linkQueryBuilder(this.queryBuilder));
        },

        setInitialSelection() {
            const alreadySelected = Array.isArray(this.allSelected) ? this.allSelected.map(item => item.id) : [];

            this.selected = this.items.filter(item => includes(alreadySelected, item[this.useKey]));

            if (this.limit) {
                this.selected = take(this.selected, this.limit);
            }
        },

        resetSelectionToCommitted() {
            this.allSelected = Array.isArray(this.alreadySelected) ? [...this.alreadySelected] : [];
            this.selected = [];
        },

        add(item) {
            if (!this.limit) {
                this.selected = [...this.selected, item];
            } else {
                this.selected = takeRight([...this.selected, item], this.limit);
            }
        },

        remove(item) {
            this.selected = filter(this.selected, search => search[this.useKey] !== item[this.useKey]);
        },

        async retrieveItems() {
            const requestId = this.latestRequestId + 1;
            this.latestRequestId = requestId;

            this.retrieving = true;

            this.$emit('retrieving', this.eventPayload);

            try {
                if (this.model) {
                    const response = await this.model.api().get(this.queryBuilder.toUrl(), { errorHandle: false });

                    if (requestId === this.latestRequestId) {
                        const responseData = response.response?.data || {};
                        const responseItems = Array.isArray(responseData.data) ? responseData.data : [];

                        this.pagination = responseData.meta || { current_page: 1, total: 0, per_page: 0 };
                        this.pagination.entity_ids = responseItems.map(item => item.id);
                    }
                } else {
                    const { data } = await axios.get(this.queryBuilder.toUrl(), { errorHandle: false });

                    if (requestId === this.latestRequestId) {
                        this.rawItems = Array.isArray(data?.data) ? data.data : [];
                        this.pagination = data?.meta || { current_page: 1, total: 0, per_page: 0 };
                    }
                }

                if (requestId === this.latestRequestId) {
                    this.setInitialSelection();
                    this.$emit('retrieved', this.eventPayload);
                }
            } catch (e) {
                if (requestId === this.latestRequestId) {
                    handleResponseError(e);
                }
            } finally {
                if (requestId === this.latestRequestId) {
                    this.retrieving = false;
                }
            }
        },

        isSelected(item) {
            if (!item || this.selected.length === 0) {
                return false;
            }

            return this.selected.map(selectedItem => selectedItem[this.useKey]).includes(item[this.useKey]);
        },

        canSelectItem(item) {
            if (!item || this.isSelected(item)) {
                return true;
            }

            if (this.limit > 1) {
                return this.allSelected.length < this.limit;
            }

            return true;
        },

        selectItem(item) {
            if (item === undefined || item === null) {
                this.toggleSelectAll();
                return;
            }

            if (this.isSelected(item)) {
                this.$emit('removing', { item, ...this.eventPayload });

                this.remove(item);

                this.$emit('removed', { item, ...this.eventPayload });

                if (this.limit > 1) {
                    this.allSelected = this.allSelected.filter(group => group.id !== item.id);
                }
            } else {
                if (this.limit > 1 && this.allSelected.length >= this.limit) {
                    this.notifyLimitReached();

                    this.selected = [...this.selected, item];

                    this.$nextTick(() => {
                        this.selected = this.selected.filter(
                            selectedItem => selectedItem[this.useKey] !== item[this.useKey]
                        );
                    });

                    return;
                }

                this.$emit('adding', { item, ...this.eventPayload });

                this.add(item);

                this.$emit('added', { item, ...this.eventPayload });

                if (this.limit > 1) {
                    this.allSelected = takeRight(uniqBy([...this.allSelected, ...this.selected], 'id'), this.limit);
                }
            }
        },

        notifyLimitReached() {
            const labels = pluralize(this.label, this.limit);

            this.$edverumToast?.open({
                type: 'is-warning',
                message: `Maximum of ${this.limit} ${labels} allowed. Please remove one of the selected ${labels} before adding a new one.`,
                duration: 4000,
            });
        },

        toggleSelectAll() {
            if (this.limit === 1 || this.selectAllDisabled) {
                return;
            }

            const { selectableItems, selectionCap } = this;
            const visibleIds = selectableItems.map(item => item[this.useKey]);

            const selectedCount = selectableItems.filter(item => this.isSelected(item)).length;
            const everythingSelected = selectionCap > 0 && selectedCount >= selectionCap;

            if (everythingSelected) {
                this.selected = [];

                if (this.limit > 1) {
                    this.allSelected = this.allSelected.filter(item => !visibleIds.includes(item[this.useKey]));
                }

                return;
            }

            this.selected = take(selectableItems, selectionCap);

            if (this.limit > 1) {
                this.allSelected = takeRight(uniqBy([...this.allSelected, ...this.selected], this.useKey), this.limit);
            }
        },

        async onFilter(filterEvent) {
            const sampleFilter = find(this.filters, ({ field }) => field === filterEvent.filter.field);

            this.$emit('filtering', { filterEvent, ...this.eventPayload });

            this.queryBuilder.page(1);

            sampleFilter.handler({
                filterEvent,
                queryBuilder: this.queryBuilder,
                items: this.items,
            });

            await this.retrieveItems();

            this.$emit('filtered', { filterEvent, ...this.eventPayload });
        },

        async onClearFilters() {
            this.filters.forEach(f => f.clearFilter());
            this.queryBuilder.page(1);
            await this.retrieveItems();
        },

        async onSort(field, order) {
            let sortingField = field;
            let sortingOrder = order;

            if (typeof field === 'object' && field !== null) {
                sortingOrder = field.sortOrder === 1 ? 'asc' : 'desc';
                sortingField = field.sortField;
            }
            this.sortingField = sortingField;
            this.sortingOrder = sortingOrder;

            if (this.sortingField) {
                this.$emit('sorting', { field: sortingField, order: sortingOrder, ...this.eventPayload });

                this.queryBuilder.clearSorts().sort(this.sortingField, this.sortingOrder);

                await this.retrieveItems();

                this.$emit('sorted', { field: sortingField, order: sortingOrder, ...this.eventPayload });
            } else {
                this.$emit('sorting', { field: sortingField, order: sortingOrder, ...this.eventPayload });

                this.queryBuilder.clearSorts();

                await this.retrieveItems();

                this.$emit('sorted', { field: sortingField, order: sortingOrder, ...this.eventPayload });
            }
        },

        // eslint-disable-next-line func-names
        search: debounce(async function (query) {
            this.$emit('searching', { query, ...this.eventPayload });

            if (query) {
                this.queryBuilder.raw({ search: query });
            } else {
                this.queryBuilder.removeRaw('search');
            }

            await this.retrieveItems();

            this.$emit('searched', { query, ...this.eventPayload });
        }, 500),

        changePage(page) {
            this.queryBuilder.setPagination({ page });

            this.retrieveItems();
        },

        setPagination(meta) {
            this.pagination = meta || { current_page: 1, total: 0, per_page: 0 };
        },

        handleFormSubmit(e) {
            e.preventDefault();
            if (!this.internallyDisabled) {
                this.choose();
            }
        },

        choose() {
            if (this.limit > 1) {
                this.selected = this.allSelected;
            }

            this.$emit('chosen', this.selected);

            if (this.closeOnChoose) {
                this.close({ resetSelection: false });
            }
        },

        close({ resetSelection = true } = {}) {
            this.isModalOpen = false;

            if (resetSelection) {
                this.resetSelectionToCommitted();
            }

            this.$emit('close');
        },
    },
};
</script>

<style lang="scss" scoped>
@import 'resources/scss/toolbox.scss';

.is-modal-select {
    .animation-content {
        border-radius: $radius-large;
        overflow: hidden !important;
    }

    .modal-select-item {
        cursor: pointer;
        border: 1px solid $border;
        border-radius: $radius;
        vertical-align: bottom;

        &.selected {
            border-color: $primary;
            box-shadow: $card-shadow;
        }
    }

    .table {
        border: 0 !important;
        outline: none;

        tr {
            cursor: pointer;
        }
    }
}

.e-modal-header {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    text-align: center;
    padding: $size-6 !important;
    background-color: var(--p-content-background);
    border-top-left-radius: $radius-large;
    border-top-right-radius: $radius-large;
    border-bottom: 1px solid var(--p-content-border-color);
}

.e-modal-title {
    font-weight: $card-header-weight;
    font-size: $size-5;
    color: var(--p-text-color);
}

.e-modal-body {
    background-color: var(--p-content-background);
    padding: $size-6;
    overflow-y: auto;

    @include until($desktop) {
        overflow-y: scroll;
    }
}

.e-modal-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: $size-6 !important;
    background-color: var(--p-content-background);
    border-top: 1px solid var(--p-content-border-color);
    border-bottom-left-radius: $radius-large;
    border-bottom-right-radius: $radius-large;
    position: sticky;
    bottom: 0;
}

.e-modal-loader {
    -webkit-overflow-scrolling: touch;
    background-color: var(--p-content-background);
    flex-grow: 1;
    flex-shrink: 1;
    overflow: auto;
}

.progress {
    border-radius: 0;
}
</style>